import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import firestoreService from '../services/firestoreService';
import authService from '../services/authService';
import { handleFirebaseError, logError } from '../utils/errorHandler';

// Hook for fetching pass-ons with real-time updates
export const usePassOns = (filters = {}) => {
  const [passOns, setPassOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Memoize filters to prevent infinite re-renders
  const memoizedFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    let unsubscribe;
    let isMounted = true;

    const setupDataListener = async () => {
      if (!isMounted) return;
      
      setLoading(true);
      setError(null);

      // Auto sign-in anonymously if no user
      if (!user) {
        try {
          await authService.signInAnonymously();
        } catch (authError) {
          console.error('Auto sign-in failed:', authError);
          if (isMounted) {
            setError('Failed to connect to database');
            setLoading(false);
          }
          return;
        }
      }

      if (!isMounted) return;

      unsubscribe = firestoreService.subscribeToPassOns(
        (data) => {
          if (isMounted) {
            setPassOns(data);
            setLoading(false);
          }
        },
        memoizedFilters
      );
    };

    setupDataListener();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, memoizedFilters]);

  return { passOns, loading, error };
};

// Hook for fetching user data with real-time updates
export const useUser = (userId) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser || !userId) {
      setUserData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = firestoreService.subscribeToUser(
      userId,
      (data) => {
        setUserData(data);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser, userId]);

  return { userData, loading, error };
};

// Hook for CRUD operations on pass-ons
export const usePassOnActions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const createPassOn = useCallback(async (passOnData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Auto sign-in if not authenticated
      let currentUser = user;
      if (!currentUser) {
        await authService.signInAnonymously();
        currentUser = authService.getCurrentUser();
      }
      
      if (!currentUser) {
        throw new Error('Unable to authenticate user');
      }
      
      const passOnId = await firestoreService.createPassOn({
        ...passOnData,
        userId: currentUser.uid
      });
      return passOnId;
    } catch (err) {
      const errorMessage = handleFirebaseError(err);
      setError(errorMessage);
      logError(err, { action: 'createPassOn', userId: user?.uid });
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const updatePassOn = useCallback(async (passOnId, updateData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Auto sign-in if not authenticated
      let currentUser = user;
      if (!currentUser) {
        await authService.signInAnonymously();
        currentUser = authService.getCurrentUser();
      }
      
      if (!currentUser) {
        throw new Error('Unable to authenticate user');
      }
      
      await firestoreService.updatePassOn(passOnId, updateData);
    } catch (err) {
      const errorMessage = handleFirebaseError(err);
      setError(errorMessage);
      logError(err, { action: 'updatePassOn', passOnId, userId: user?.uid });
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const deletePassOn = useCallback(async (passOnId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Auto sign-in if not authenticated
      let currentUser = user;
      if (!currentUser) {
        await authService.signInAnonymously();
        currentUser = authService.getCurrentUser();
      }
      
      if (!currentUser) {
        throw new Error('Unable to authenticate user');
      }
      
      await firestoreService.deletePassOn(passOnId);
    } catch (err) {
      const errorMessage = handleFirebaseError(err);
      setError(errorMessage);
      logError(err, { action: 'deletePassOn', passOnId, userId: user?.uid });
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user]);

  return {
    createPassOn,
    updatePassOn,
    deletePassOn,
    loading,
    error
  };
};

// Hook for pass-on history
export const usePassOnHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setHistory([]);
      setLoading(false);
      return;
    }

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const historyData = await firestoreService.getPassOnHistory(user.uid);
        setHistory(historyData);
      } catch (err) {
        const errorMessage = handleFirebaseError(err);
        setError(errorMessage);
        logError(err, { action: 'fetchHistory', userId: user.uid });
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user]);

  const addToHistory = useCallback(async (historyData) => {
    if (!user) throw new Error('User must be authenticated');
    
    try {
      const historyId = await firestoreService.createPassOnHistory({
        ...historyData,
        fromUserId: user.uid
      });
      
      // Update local state
      const newHistoryItem = {
        id: historyId,
        ...historyData,
        fromUserId: user.uid,
        passedAt: new Date()
      };
      setHistory(prev => [newHistoryItem, ...prev]);
      
      return historyId;
    } catch (err) {
      const errorMessage = handleFirebaseError(err);
      logError(err, { action: 'addToHistory', userId: user.uid });
      throw new Error(errorMessage);
    }
  }, [user]);

  return {
    history,
    loading,
    error,
    addToHistory
  };
};
